import { useState, useEffect, useCallback } from "react";
import Api from "@/api";
import { showErrorToast } from "turboui";
import { Person, SearchScope } from ".";

interface UsePeopleSearch<T> {
  scope: SearchScope;
  transformResult?: (person: Person) => T; // transformResult must be memoized
  ignoredIds?: string[]; // ignoredIds must be memoized
}

// This matches PersonField.SearchData from turboui
interface SearchData<T> {
  people: T[];
  onSearch: (query: string) => Promise<void>;
}

export function usePersonFieldSearch<T>(hookParams: UsePeopleSearch<T>): SearchData<T> {
  const [people, setPeople] = useState<T[]>([]);

  const onSearch = useCallback(
    async (query: string) => {
      try {
        const transform = hookParams.transformResult || ((person) => person as unknown as T);

        const ignoredIds = (hookParams.ignoredIds || []).filter((id): id is string => Boolean(id));
        const trimmedQuery = query.trim();

        const result = await Api.people.search({
          query: trimmedQuery,
          ignoredIds,
          searchScopeType: hookParams.scope.type,
          searchScopeId: hookParams.scope.id,
        });

        const fetchedPeople = result.people || [];
        const transformedPeople = fetchedPeople
          .filter((person): person is Person => !!person)
          .map((person) => transform(person)) as T[];
        setPeople(transformedPeople);
      } catch {
        showErrorToast("Couldn't load people", "Please try again.");
      }
    },
    [hookParams.scope.type, hookParams.scope.id, hookParams.ignoredIds, hookParams.transformResult],
  );

  // Load initial people on mount
  useEffect(() => {
    onSearch("");
  }, [onSearch]);

  return { people, onSearch };
}
